<template>
    <div
        v-if="file.status !== STATUS_DELETED"
        class="uploader-input-element bg-white border br-4 font-size-110 position-relative"
        :class="{
            ['local-id-' + file.localId]: true,
            [file.status]: true,
            'mb-2': !isLast,
        }"
    >
        <div class="px-3 py-2 d-flex align-items-center justify-content-between flex-row">
            <div
                class="d-inline-flex py-1 pr-4 justify-content-between flex-wrap flex-sm-nowrap flex-basis-100 flex-shrink-1 ml-n2_ mb-n2 flex-grow-1-unimportant"
                style="min-width: 0"
            >
                <div class="d-flex align-items-center ml-2_ mb-2 overflow-hidden">
                    <div
                        v-if="
                            showStatusWaiting ||
                            file.status === STATUS_DELETING ||
                            file.status === STATUS_LOADING ||
                            file.status === STATUS_SETTING_PASSWORD
                        "
                        class="d-flex"
                        :class="file.status === STATUS_SETTING_PASSWORD ? 'mr-2_' : 'mr-3'"
                    >
                        <span class="spin-circle-basic opacity-65"></span>
                    </div>

                    <template v-if="showStatusWaiting">
                        <span class="overflow-hidden text-overflow-ellipsis">{{ 'Initializing' | trans }}...</span>
                    </template>
                    <template v-else>
                        <span v-if="file.status === STATUS_LOADING" class="badge badge-info mr-2_">
                            <template v-if="file.type === TYPE_FILE_UPLOAD">{{ 'Uploading' | trans }}</template>
                            <template v-else>{{ 'Adding' | trans }}</template>
                        </span>
                        <i v-else-if="file.status === STATUS_PASSWORD_MISSING" class="far fa-lock mr-2_"></i>
                        <span v-else-if="file.status === STATUS_DELETING" class="mr-2_ badge badge-danger">
                            {{ 'Removing' | trans }}
                        </span>
                        <span
                            v-else-if="file.status === STATUS_FAILED"
                            class="mr-2_ badge badge-danger"
                            :class="file.lastEventData?.error === STATUS_TOO_MANY_INPUT && 'premium-bg'"
                        >
                            <template v-if="file.size === 0">{{ 'Your file is empty' | trans }}</template>
                            <template v-else-if="file.lastEventData?.error === STATUS_TOO_MANY_INPUT">
                                <span v-if="isSingleInput">{{ 'Single file feature' | trans }}</span>
                                <span v-else>{{ 'Premium required' | trans }}</span>
                            </template>
                            <template v-else-if="file.type === TYPE_FILE_UPLOAD">{{ 'Upload failed' | trans }}</template>
                            <template v-else>{{ 'Adding failed' | trans }}</template>
                        </span>
                        <span class="overflow-hidden text-overflow-ellipsis white-space-nowrap" :title="file.name">{{
                            file.name
                        }}</span>
                    </template>
                </div>

                <div v-if="statusBadges.length > 0" class="ml-2_ mb-2 mt-n1 flex-sm-shrink-0 test">
                    <template v-for="(badge, idx) in statusBadges">
                        <FileBadge
                            :key="idx + badge.classes"
                            :add-margin-class="idx < statusBadges.length - 1"
                            :icon="badge.icon"
                            :is-icon-before-text="badge.iconBefore === true"
                            :text="badge.text"
                            :title="badge.title"
                            :classes="badge.classes + ' mt-1'"
                        >
                            <template v-slot:default v-if="badge.icon === TYPE_GDRIVE || badge.icon === TYPE_DROPBOX">
                                <svg
                                    v-if="badge.icon === TYPE_GDRIVE"
                                    width="15px"
                                    viewBox="0 0 87.3 78"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
                                        fill="#0066da"
                                    />
                                    <path
                                        d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
                                        fill="#00ac47"
                                    />
                                    <path
                                        d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
                                        fill="#ea4335"
                                    />
                                    <path
                                        d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
                                        fill="#00832d"
                                    />
                                    <path
                                        d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
                                        fill="#2684fc"
                                    />
                                    <path
                                        d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
                                        fill="#ffba00"
                                    />
                                </svg>
                                <svg
                                    v-if="badge.icon === TYPE_DROPBOX"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 43 40"
                                    version="1.1"
                                    width="15px"
                                >
                                    <path
                                        d="m12.5 0l-12.5 8.1 8.7 7 12.5-7.8-8.7-7.3zm-12.5 21.9l12.5 8.2 8.7-7.3-12.5-7.7-8.7 6.8zm21.2 0.9l8.8 7.3 12.4-8.1-8.6-6.9-12.6 7.7zm21.2-14.7l-12.4-8.1-8.8 7.3 12.6 7.8 8.6-7zm-21.1 16.3l-8.8 7.3-3.7-2.5v2.8l12.5 7.5 12.5-7.5v-2.8l-3.8 2.5-8.7-7.3z"
                                        fill="#007EE5"
                                    />
                                </svg>
                            </template>
                        </FileBadge>
                    </template>
                </div>
            </div>

            <div
                v-if="file.status === STATUS_DONE || showRemoveFileButton"
                class="d-inline-flex align-self-baseline"
                style="padding-top: 0.125rem"
            >
                <button
                    v-if="file.status === STATUS_DONE"
                    :title="'Show more information about this file' | trans"
                    class="btn btn-light border icon-25 p-0 mr-3"
                    :class="{ 'visibility-hidden': fileMetaBadges.length === 0 }"
                    @click.stop.prevent="toggleInfo"
                >
                    <i class="far fa-info-circle"></i>
                </button>
                <button
                    v-if="showRemoveFileButton"
                    :title="'Remove file from task' | trans"
                    class="btn btn-danger icon-25 p-0"
                    :class="{ disabled: file.status === STATUS_SETTING_PASSWORD }"
                    :disabled="file.status === STATUS_SETTING_PASSWORD"
                    @click.stop.prevent="removeFile"
                >
                    <i class="fa fa-times"></i>
                </button>
            </div>
        </div>

        <div
            v-if="showInfo && fileMetaBadges.length > 0"
            class="w-100 d-flex flex-wrap py-3 px-3 border-top bg-cheap-white br-4 rounded-top-0"
        >
            <div class="mb-n1">
                <FileBadge
                    v-for="(badge, idx) in fileMetaBadges"
                    :key="idx + badge.classes"
                    :icon="badge.icon"
                    :is-icon-before-text="badge.iconBefore === true"
                    :text="badge.text"
                    :title="badge.title"
                    :classes="badge.classes + ' mb-1'"
                ></FileBadge>
            </div>
        </div>

        <div
            v-if="file.status === STATUS_PASSWORD_MISSING || file.status === STATUS_SETTING_PASSWORD"
            class="py-3 w-100 px-3 border-top bg-cheap-white br-4 rounded-top-0"
        >
            <div>
                <p class="font-size-110 font-weight-bold mb-2">{{ 'Your file is password protected' | trans }}</p>
                <p class="font-size-110 mb-3">
                    {{ 'Please enter your password so we can continue processing your file.' | trans }}
                </p>
            </div>
            <div class="d-flex align-items-center ml-n3 flex-wrap">
                <span class="ml-3">{{ 'Enter file password' | trans }}:</span>
                <div class="ml-3 mr-n2_ mb-n2 d-flex flex-wrap">
                    <input
                        class="border py-1 px-2 br-4 mr-2_ mb-2"
                        type="password"
                        :name="`input-decrypt-password-${file.localId}`"
                        :placeholder="'Password' | trans"
                        :disabled="file.status === STATUS_SETTING_PASSWORD"
                        @keydown.enter.stop.prevent="setFilePassword"
                        ref="passwordInputField"
                        v-model="passwordInput"
                    />
                    <ButtonWithLoadingState
                        data-file-row-password-submit
                        :text="'Submit' | trans"
                        icon="far fa-unlock-alt mr-1_"
                        class="btn btn-info py-1 mr-2_ mb-2"
                        :is-state-loading="file.status === STATUS_SETTING_PASSWORD"
                        @click.stop.prevent="setFilePassword"
                    ></ButtonWithLoadingState>
                </div>
            </div>
        </div>

        <div
            class="p-0 w-100 left-0 bottom-0 position-absolute overflow-hidden br-4 rounded-top-0 bg-cheap-white border-top"
            style="height: 4px"
            :key="progressBarKey"
        >
            <div class="progress-bar h-100" :class="progressBarClass" :style="{ width: progressBarWidth }"></div>
        </div>
    </div>
</template>

<script>
import {
    INPUT_STATUS_UPLOADER_DELETED,
    INPUT_STATUS_UPLOADER_DELETING,
    INPUT_STATUS_UPLOADER_DONE,
    INPUT_STATUS_UPLOADER_FAILED,
    INPUT_STATUS_UPLOADER_LOADING,
    INPUT_STATUS_UPLOADER_PASSWORD_MISSING,
    INPUT_STATUS_UPLOADER_QUEUED,
    INPUT_STATUS_UPLOADER_SETTING_PASSWORD,
    INPUT_STATUS_UPLOADER_SUBMITTING,
    INPUT_TYPE_DROPBOX,
    INPUT_TYPE_EXTERNAL_URL,
    INPUT_TYPE_FILE_UPLOAD,
    INPUT_TYPE_GDRIVE,
    INPUT_TYPE_INPUT_ID,
} from '../../core/consts';
import TransFilter from '../../../../qaamgo/modules/sat/components/mixins/TransFilter';
import { translate } from '../../../../qaamgo/helper/translate';
import { testIsNonEmptyString, testIsNumber } from '../../../../lib/test-and-assert/test-base';
import TextInput from '../../../../qaamgo/modules/sat/components/form/TextInput';
import ButtonWithLoadingState from '../../../../qaamgo/modules/sat/components/form/ButtonWithLoadingState';
import { formatting } from '../../../../qaamgo/helper/formatting';
import FileBadge from './FileBadge';
import { pageNotification } from '../../../../qaamgo/helper/notify';
import { API_ERROR_URL_TOO_LONG, API_ERROR_LIMITS_TOO_MANY_INPUTS } from '../../../api/consts';

export default {
    name: 'FileRow',
    components: { FileBadge, ButtonWithLoadingState, TextInput },
    mixins: [TransFilter],
    props: {
        file: {
            type: Object,
            required: true,
        },
        isLast: {
            type: Boolean,
            required: true,
        },
    },
    computed: {
        progressBarKey() {
            // workaround - force vue to re-render progress bar by changing the key
            return this.showStatusWaiting ? 1 : 0;
        },
        showRemoveFileButton() {
            const statuses = [this.STATUS_DONE, this.STATUS_PASSWORD_MISSING, this.STATUS_SETTING_PASSWORD];

            return (
                statuses.includes(this.file.status) ||
                (this.file.status === this.STATUS_LOADING && this.file.type === this.TYPE_FILE_UPLOAD)
            );
        },
        progressBarWidth() {
            if (this.file.type === this.TYPE_FILE_UPLOAD && this.file.status === this.STATUS_LOADING) {
                return `${this.file.progressPercent}%`;
            }

            return '100%';
        },
        progressBarClass() {
            if (this.file.status === this.STATUS_DONE) {
                return 'bg-success';
            }

            if (this.file.status === this.STATUS_FAILED || this.file.status === this.STATUS_DELETING) {
                return 'bg-danger';
            }

            if (this.file.status === this.STATUS_PASSWORD_MISSING) {
                return 'bg-warning';
            }

            return 'progressing';
        },
        currentBitrateFormatted() {
            return formatting.bitrate(this.file.progressAverageBitrate);
        },
        fileMetaBadges() {
            if (this.file.status !== this.STATUS_DONE) {
                return [];
            }

            // TODO add more badges for meta and info stuff!
            let badges = [];

            // TODO move file extension badge logic to helper?
            if (this.file.name.trim().toLowerCase().endsWith('.pdf')) {
                badges = [
                    ...badges,
                    {
                        text: 'PDF',
                        classes: 'badge-white border',
                        icon: 'far fa-file-pdf',
                    },
                ];

                if (testIsNonEmptyString(this.file.metadata?.version)) {
                    badges = [
                        ...badges,
                        {
                            text: `PDF Version ${this.file.metadata.version}`,
                            classes: 'badge-white border',
                        },
                    ];
                }
            }

            if (testIsNumber(this.file.metadata?.pages)) {
                badges = [
                    ...badges,
                    {
                        text:
                            this.file.metadata?.pages === 1
                                ? translate('1 page')
                                : translate('%count% pages', { '%count%': this.file.metadata?.pages }),
                        classes: 'badge-info text-white',
                        icon: 'far fa-copy',
                    },
                ];
            }

            return badges;
        },
        statusBadges() {
            if (
                this.file.type === this.TYPE_EXTERNAL_URL &&
                this.file.status === this.STATUS_FAILED &&
                this.file.lastEventData?.error === API_ERROR_URL_TOO_LONG
            ) {
                return [
                    {
                        text: translate('Url too long'),
                        classes: 'badge-danger text-white',
                        icon: 'far fa-exclamation-triangle',
                        iconBefore: true,
                    },
                ];
            }

            if (
                this.showStatusWaiting ||
                this.file.status === this.STATUS_FAILED ||
                this.file.status === this.STATUS_DELETING
            ) {
                return [];
            }

            let badges = [];

            if (
                this.file.status === this.STATUS_PASSWORD_MISSING ||
                this.file.status === this.STATUS_SETTING_PASSWORD
            ) {
                badges = [
                    ...badges,
                    {
                        text: translate('Password required'),
                        classes: 'badge-danger text-white',
                        icon: 'far fa-lock',
                        iconBefore: true,
                    },
                ];
            }

            if (this.file.metadata?.damaged === true) {
                badges = [
                    ...badges,
                    {
                        text: translate('Damaged'),
                        classes: 'badge-warning',
                        icon: 'far fa-exclamation-triangle',
                        iconBefore: true,
                        title: translate(
                            'There may be something wrong with your file, we might not be able to process it'
                        ),
                    },
                ];
            }
            if (this.file.metadata?.unsupported_encryption === true) {
                this.$parent.failInput(this.file.localId);
                this.file.status = this.STATUS_FAILED;
                badges = [
                    ...badges,
                    {
                        text: translate('Unsupported encryption'),
                        classes: 'badge-warning',
                        icon: 'far fa-exclamation-triangle',
                        iconBefore: true,
                        title: translate(
                            'There may be something wrong with your file, we might not be able to process it'
                        ),
                    },
                ];
            }

            if (this.file.name.trim().toLowerCase().endsWith('.pdf') && this.ocrAvailable()) {
                if (this.file.metadata?.textpages_percentage < 30) {
                    badges = [
                        ...badges,
                        {
                            text: 'OCR recommended',
                            classes: 'badge-warning',
                            icon: 'far fa-exclamation-triangle',
                            iconBefore: true,
                        },
                    ];
                }
            }

            if (
                (this.file.status === this.STATUS_DONE ||
                    this.file.status === this.STATUS_PASSWORD_MISSING ||
                    this.file.status === this.STATUS_SETTING_PASSWORD) &&
                this.file.size_readable
            ) {
                badges = [
                    ...badges,
                    {
                        text: this.file.size_readable,
                        classes: 'bg-gray-light',
                        title: translate('File size'),
                    },
                ];
            }

            if (
                this.file.status === this.STATUS_LOADING &&
                this.file.type === this.TYPE_FILE_UPLOAD &&
                this.file.progressText
            ) {
                badges = [
                    ...badges,
                    {
                        text: this.file.progressText,
                        classes: 'bg-gray-light',
                        icon: 'far fa-clock',
                        iconBefore: true,
                    },
                ];
            }

            let fileTypeBadge = {
                classes: 'bg-gray-light',
                icon: this.fileTypeIcon,
                title: this.fileTypeTitle,
            };

            if (this.file.status === this.STATUS_LOADING && this.file.type === this.TYPE_FILE_UPLOAD) {
                fileTypeBadge.text = this.currentBitrateFormatted;
            }

            badges = [...badges, fileTypeBadge];

            return badges;
        },
        showStatusWaiting() {
            return this.file.status === this.STATUS_QUEUED || this.file.status === this.STATUS_SUBMITTING;
        },
        fileTypeIcon() {
            if (this.file.type === INPUT_TYPE_DROPBOX) {
                return INPUT_TYPE_DROPBOX;
            }

            if (this.file.type === INPUT_TYPE_GDRIVE) {
                return INPUT_TYPE_GDRIVE;
            }

            if (this.file.type === INPUT_TYPE_FILE_UPLOAD) {
                return 'far fa-upload';
            }

            return 'far fa-link';
        },
        fileTypeTitle() {
            if (this.file.type === INPUT_TYPE_DROPBOX) {
                return translate('Dropbox');
            }

            if (this.file.type === INPUT_TYPE_GDRIVE) {
                return translate('Google Drive');
            }

            if (this.file.type === INPUT_TYPE_FILE_UPLOAD) {
                return translate('File upload');
            }

            return translate('Remote Link');
        },
        isSingleInput() {
            return global.qgUploader.isSingleInput();
        }
    },
    methods: {
        removeFile() {
            if (this.file.type === this.TYPE_FILE_UPLOAD && this.file.status === this.STATUS_LOADING) {
                this.$parent.cancelInput(this.file.localId);
            } else {
                this.$parent.deleteInput(this.file.localId);
            }
        },
        toggleInfo() {
            this.showInfo = !this.showInfo;
        },
        setFilePassword() {
            let password = this.passwordInput;

            if (!testIsNonEmptyString(password)) {
                // fallback: look directly into the password input field
                // in case the value was autofilled by the browser
                password = this.$refs.passwordInputField.value;
            }

            if (!testIsNonEmptyString(password)) {
                pageNotification.error('The file is password protected. Please provide the password.');

                return;
            }

            this.$parent.setPassword(this.file.localId, password);
        },
        ocrAvailable() {
            const ocrElements = document.getElementsByName('ocr');
            let ocrAvailable = false;
            for (const ocrElement of ocrElements) {
                ocrAvailable = ocrElement.offsetParent !== null;
            }

            return ocrAvailable;
        },
    },
    data() {
        return {
            // TODO clean up here?
            STATUS_QUEUED: INPUT_STATUS_UPLOADER_QUEUED,
            STATUS_SUBMITTING: INPUT_STATUS_UPLOADER_SUBMITTING,
            STATUS_LOADING: INPUT_STATUS_UPLOADER_LOADING,
            STATUS_DONE: INPUT_STATUS_UPLOADER_DONE,
            STATUS_SETTING_PASSWORD: INPUT_STATUS_UPLOADER_SETTING_PASSWORD,
            STATUS_PASSWORD_MISSING: INPUT_STATUS_UPLOADER_PASSWORD_MISSING,
            STATUS_DELETING: INPUT_STATUS_UPLOADER_DELETING,
            STATUS_DELETED: INPUT_STATUS_UPLOADER_DELETED,
            STATUS_FAILED: INPUT_STATUS_UPLOADER_FAILED,
            STATUS_TOO_MANY_INPUT: API_ERROR_LIMITS_TOO_MANY_INPUTS,

            TYPE_EXTERNAL_URL: INPUT_TYPE_EXTERNAL_URL,
            TYPE_FILE_UPLOAD: INPUT_TYPE_FILE_UPLOAD,
            TYPE_GDRIVE: INPUT_TYPE_GDRIVE,
            TYPE_INPUT_ID: INPUT_TYPE_INPUT_ID,
            TYPE_DROPBOX: INPUT_TYPE_DROPBOX,
            showInfo: false,
            infoToggleIcon: 'fa-chevron-down',
            passwordInput: '',
        };
    },
};
</script>

<style lang="scss">
@import '~sass-material-colors/sass/sass-material-colors';

@import '../../../../../scss/abstracts/mixins';
@import '../../../../../scss/abstracts/functions';
@import '../../../../../scss/abstracts/variables';
@import '../../../../../../../../../../assets/scss/abstracts/variables';

@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins';

$d: 800px;
$angle: 120deg;
$sin: 0.866;
@keyframes BackgroundScroll {
    0% {
        background-position: calc(#{$d} / #{$sin}) 0;
    }
}

.progress-bar {
    background: $info;
}

.progress-bar.progressing {
    background: repeating-linear-gradient($angle, $info, lighten($info, 20%), 50% $info $d);
    background-size: calc(#{$d} / #{$sin}) 100%;
    animation: BackgroundScroll 2s linear infinite reverse;
    transition: width ease 1s;
}

.icon-25 {
    width: 25px;
    height: 25px;
}
.premium-bg {
    background-color: #1e1e1e;
}
</style>
